import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';
import { linkOptions } from '@tanstack/react-router';
import { useAdvertiserPermissions, useAgencyPermissions, useUserRoles } from '@anytag/auth';
import { AssociateCompanyType } from '@anytag/graphql';
import { TableCell } from '@anytag/shared-atoms';
import { SpreadButton } from '@anytag/shared-molecules';
import { getUserRoleString, formatIntNumber } from '@anytag/shared-utils';
import { AssociatedAccountType } from '../types';

interface Props {
  onDelete?: (account: AssociatedAccountType) => void;
}

export const useAssociatedAccountsTable = ({ onDelete }: Props) => {
  const { t } = useTranslation();
  const { isAdminStaff } = useUserRoles();
  const { hideEditAgencyBtn, hideDeleteAgencyBtn } = useAgencyPermissions();
  const { hideEditAdvertiserBtn, hideDeleteAdvertiserBtn } = useAdvertiserPermissions();

  const columns: ColumnDef<AssociatedAccountType>[] = [
    {
      accessorKey: 'name',
      header: t<string>('HeaderColumn.CompanyName'),
      cell: ({ row }) => {
        const { id, name, companyType } = row.original;
        const isAdvertiser = companyType === AssociateCompanyType.ADVERTISER;
        const disabledEditLink = isAdvertiser ? hideEditAdvertiserBtn : hideEditAgencyBtn;
        const disabledDeleteLink = isAdvertiser ? hideDeleteAdvertiserBtn : hideDeleteAgencyBtn;
        const editLink = linkOptions({
          to: isAdvertiser ? '/advertiser/$id' : '/agency/$id',
          params: { id: id.toString() }
        });

        return (
          <div className="account-name-column">
            <TableCell.Link {...editLink} label={name} disabled={disabledEditLink} />
            <SpreadButton
              collapseFrom={0}
              moreButtonProps={{ disabled: hideDeleteAdvertiserBtn && hideEditAdvertiserBtn }}
              options={[
                {
                  label: t('Edit'),
                  icon: 'edit',
                  ...editLink,
                  hidden: disabledEditLink,
                  tooltip: { help: t('Button.Edit') }
                },
                {
                  icon: 'delete',
                  label: t('Delete'),
                  disabled: !isAdminStaff,
                  hidden: disabledDeleteLink,
                  onClick: () => onDelete?.(row.original),
                  tooltip: { help: t('Button.Delete') }
                }
              ]}
            />
          </div>
        );
      }
    },
    {
      accessorKey: 'companyType',
      header: t<string>('HeaderColumn.Company Type'),
      cell: ({ row }) => <TableCell.Text value={t(`Option.${getUserRoleString(row.original.companyType)}`)} />
    },
    {
      accessorKey: 'phoneNumber',
      header: t<string>('HeaderColumn.Phone Number'),
      cell: ({ row }) => <TableCell.Text value={row.original.phoneNumber || '-'} />
    },
    {
      accessorKey: 'usersCount',
      header: t<string>('HeaderColumn.User'),
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.usersCount)} />,
      meta: { align: 'right' }
    }
  ];

  return { columns };
};
